/* eslint-disable no-underscore-dangle */
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { ApplicantStateful, coBorrowersPath } from './applicant-stateful.class';
import { Applicant } from './applicant.class';
import { JointApplicant } from './joint-applicant.class';
import { JointCreditValidation } from './joint-credit-validation.class';

/** Manager class for add/remove joint applicant */
export class JointCreditManager {
  /**
   * Single tone class
   */
  private static readonly instance: { [protocol: string]: JointCreditManager } = {};

  /**
   * Reference to the main applicant (primary borrower)
   */
  private _primaryApplicant: Applicant;

  /**
   * Reference to validation logic
   */
  private readonly _validation: JointCreditValidation;

  /**
   *  Dictionary representative all co applicants in the bundle
   */
  private applicantsMap: { [entityId: string]: Applicant } = {};

  /**
   * Ordered list of all co applicant in the bundle
   */
  public allApplicants: Applicant[] = [];

  /**
   * List of joint applicant in pair
   */
  public jointApplicants: JointApplicant[] = [];

  /**
   * Reference to the main applicant
   */
  public get primaryBorrower(): Applicant {
    // eslint-disable-next-line no-underscore-dangle
    return this._primaryApplicant;
  }

  /**
   * Calculate the maximum number of joint pair based on number of co applicant
   * #of Joint Credit Boxes = Quotient of (# of total applicants/2)
   */
  public get maxNumberOfJointPair(): number {
    const pair = 2;
    return Math.floor(this.allApplicants.length / pair);
  }

  /**
   * Number of co applicants in the bundle
   */
  private get coApplicantSize(): number {
    return StatefulValue.arraySize(coBorrowersPath(this.backendProtocol));
  }

  constructor(private readonly backendProtocol: string) {
    if (JointCreditManager.instance[backendProtocol]) {
      return JointCreditManager.instance[backendProtocol];
    }

    JointCreditManager.instance[backendProtocol] = this;

    this._validation = new JointCreditValidation(this);
    this.refreshApplicantList();
  }

  /**
   * Returns main borrower in the pair
   * @param jointed co applicant with a main jointed borrower
   */
  public getMainApplicant(jointed: Applicant): Applicant {
    return jointed && this.allApplicants.find((a) => a.entityId === jointed.parentId);
  }

  public getMainApplicantByChildIndex(index: number): Applicant {
    const jointed = this.allApplicants.find((a) => a.coBorrowerIndex === index);
    return jointed && this.getMainApplicant(jointed);
  }

  /**
   * To find the jointed 'child' applicant given the main applicant as input
   * @param main main applicant in the joint credit pair
   */
  public getJointedApplicant(main: Applicant): Applicant {
    return this.allApplicants.find((a) => a.hasParent && a.parentId === main.entityId);
  }

  public removeAllJointCredit(): void {
    for (const applicant of this.allApplicants) {
      if (applicant.parentId) {
        applicant.removeParent();
      }
    }
  }

  /**
   * There should be at least one pair of joint applicant
   */
  public get validation(): JointCreditValidation {
    return this._validation;
  }

  public refreshWhenCoBorrowerChange(): void {
    new ApplicantStateful(this.backendProtocol, 0, false).firstName.afterPatch(this.callback);
  }

  public unsubscribe(): void {
    new ApplicantStateful(this.backendProtocol, 0, false).firstName.unsubscribe(this.callback);
  }

  public isJoint(applicant: Applicant): boolean {
    return !!this.jointApplicants.find((a) => Applicant.areSame(applicant, a.applicant) || Applicant.areSame(applicant, a.parent));
  }

  public refreshApplicantList(): void {
    this.buildApplicantList();
    this.buildJointApplicant();
  }

  private buildApplicantList(): void {
    this.applicantsMap = {};
    this.allApplicants = [];
    this._primaryApplicant = new Applicant(ApplicantStateful.PrimaryApplicant(this.backendProtocol), -1);
    this.addApplicant(this._primaryApplicant);

    for (let i = 0; i < this.coApplicantSize; i++) {
      this.addApplicant(new Applicant(ApplicantStateful.CoApplicant(this.backendProtocol, i), i));
    }
  }

  private buildJointApplicant(): void {
    this.jointApplicants = [];

    if (this.maxNumberOfJointPair < 1) {
      return;
    }

    for (let i = 0; i < this.maxNumberOfJointPair; i++) {
      this.jointApplicants.push(new JointApplicant(this));
    }

    let j = 0;
    for (const applicant of this.allApplicants) {
      if (j >= this.maxNumberOfJointPair) {
        break;
      }

      if (applicant.hasParent) {
        const parent = this.applicantsMap[applicant.parentId];
        if (parent) {
          this.jointApplicants[j++] = new JointApplicant(this, parent, applicant);
        } else {
          applicant.removeParent();
        }
      }
    }
  }

  private addApplicant(applicant: Applicant): void {
    this.allApplicants.push(applicant);
    if (!this.applicantsMap[applicant.entityId]) {
      this.applicantsMap[applicant.entityId] = applicant;
    }
  }

  /**
   * A call back function to execute when state is changed
   */
  private readonly callback = () => {
    setTimeout(() => {
      this.refreshApplicantList();
    }, 0);
  };

  public destoryJcm(backendProtocol: string): void {
    if (JointCreditManager.instance[backendProtocol]) {
      JointCreditManager.instance[backendProtocol] = undefined;
    }
  }
}
